// Types + validation for the two-step "Add new dish" flow.
// Mirrors AddIngredient/types.ts so the wizard shell + Step 2 (Unit
// conversion) can be reused; Step 1 is a superset of the ingredient
// fields with a Recipe & ingredients composition and Recipe instructions.

import type { DietType } from "../../Ingredient/types";
import {
  MICRO_NUTRIENT_BACKEND_KEY,
  MICRO_NUTRIENT_FIELDS,
  MicroNutrients,
  initialMicroNutrients,
} from "../../Ingredient/AddIngredient/types";
import type { DishDetail } from "../types";

// Re-export so consumers (page + step) can pull the micro definitions from a
// single dish-side entry point without also reaching into the ingredient
// module. The field list itself is shared: dishes use the same 29 micros.
export {
  MICRO_NUTRIENT_BACKEND_KEY,
  MICRO_NUTRIENT_FIELDS,
  initialMicroNutrients,
};
export type { MicroNutrientKey, MicroNutrients } from "../../Ingredient/AddIngredient/types";

export const createId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/* ── Step 1: Details & nutrition ─────────────────────────────── */

// Per-100g nutrition baseline the row uses to rescale as the user tweaks
// quantity / unit. Shape mirrors the calculator's `IngredientNutrition`
// so both flows can call the same `computeRowNutrition` helper.
export interface RecipeIngredientNutrition {
  cal: number;
  protein: number;
  grossProtein: number;
  fat: number;
  carb: number;
  netCarb: number;
  gl: number;
}

export interface RecipeIngredientMeasurement {
  unit_type: string;
  measure_gram: number;
}

export interface RecipeIngredientRow {
  id: string;
  name: string;
  imageUrl: string | null;
  quantity: string;
  unit: string;
  cal: string;
  carb: string;
  netCarb: string;
  fats: string;
  aProt: string;
  gl: string;
  oil: string;
  cpf: string;
  // Ingredient metadata — populated when the user picks a row from the
  // admin search (either via the Dish calculator's Apply or directly in
  // the Recipe & ingredients table). Enables live nutrition + unit
  // scaling and provides the `food_id` submit payloads need.
  foodId?: number;
  baseQuantity?: number;
  measurements?: RecipeIngredientMeasurement[];
  nutritionPer100g?: RecipeIngredientNutrition;
  grossProtein?: string;
}

export const createEmptyIngredientRow = (): RecipeIngredientRow => ({
  id: createId(),
  name: "",
  imageUrl: null,
  quantity: "",
  unit: "",
  cal: "",
  carb: "",
  netCarb: "",
  fats: "",
  aProt: "",
  gl: "",
  oil: "",
  cpf: "",
});

export interface CookingFatBand {
  id: string;
  minQty: string;
  maxQty: string;
  fat: string;
}

export const createEmptyCookingFatBand = (): CookingFatBand => ({
  id: createId(),
  minQty: "",
  maxQty: "",
  fat: "",
});

export interface DetailsNutritionData {
  photo: File | null;
  photoPreview: string | null;
  name: string;
  unit: string;
  baseQty: string;
  category: string;
  type: string;
  calories: string;
  adjProtein: string;
  grossProtein: string;
  carbs: string;
  netCarbs: string;
  fat: string;
  gl: string;
  fiber: string;
  micro: MicroNutrients;
  cookingFatDynamic: boolean;
  cookingFatBands: CookingFatBand[];
  recipeIngredients: RecipeIngredientRow[];
  recipeInstructions: string;
  tags: string[];
}

export interface DetailsNutritionErrors {
  name?: string;
  unit?: string;
  baseQty?: string;
  category?: string;
  type?: string;
  calories?: string;
  adjProtein?: string;
  grossProtein?: string;
  carbs?: string;
  netCarbs?: string;
  fat?: string;
  gl?: string;
  fiber?: string;
  tags?: string;
}

export const initialDetailsNutrition: DetailsNutritionData = {
  photo: null,
  photoPreview: null,
  name: "",
  unit: "",
  baseQty: "",
  category: "",
  type: "",
  calories: "",
  adjProtein: "",
  grossProtein: "",
  carbs: "",
  netCarbs: "",
  fat: "",
  gl: "",
  fiber: "",
  micro: { ...initialMicroNutrients },
  cookingFatDynamic: false,
  cookingFatBands: [createEmptyCookingFatBand()],
  recipeIngredients: [createEmptyIngredientRow()],
  recipeInstructions: "",
  tags: [],
};

const isNonEmpty = (v: string) => v.trim().length > 0;
const isPositiveNumber = (v: string) => {
  if (!isNonEmpty(v)) return false;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0;
};

export const validateDetailsNutrition = (
  data: DetailsNutritionData,
): DetailsNutritionErrors => {
  const errors: DetailsNutritionErrors = {};
  if (!isNonEmpty(data.name)) errors.name = "Dish name is required";
  if (!isNonEmpty(data.unit)) errors.unit = "Unit is required";
  if (!isPositiveNumber(data.baseQty)) errors.baseQty = "Enter a valid quantity";
  if (!isNonEmpty(data.category)) errors.category = "Category is required";
  if (!isNonEmpty(data.type)) errors.type = "Type is required";
  if (!isPositiveNumber(data.calories)) errors.calories = "Enter a valid number";
  if (!isPositiveNumber(data.adjProtein)) errors.adjProtein = "Enter a valid number";
  if (!isPositiveNumber(data.grossProtein))
    errors.grossProtein = "Enter a valid number";
  if (isNonEmpty(data.carbs) && !isPositiveNumber(data.carbs))
    errors.carbs = "Enter a valid number";
  if (!isPositiveNumber(data.netCarbs)) errors.netCarbs = "Enter a valid number";
  if (!isPositiveNumber(data.fat)) errors.fat = "Enter a valid number";
  if (!isPositiveNumber(data.gl)) errors.gl = "Enter a valid number";
  return errors;
};

/* ── Step 2: Unit conversion ────────────────────────────────── */

export interface ConversionRow {
  id: string;
  locked: boolean;
  unit: string;
  grams: string;
}

export interface UnitConversionData {
  rows: ConversionRow[];
}

export interface UnitConversionErrors {
  rows?: Record<string, { unit?: string; grams?: string }>;
}

export const buildBaseConversionRows = (unit: string): ConversionRow[] => {
  const u = unit.trim().toLowerCase();
  if (u === "ml") {
    return [
      { id: createId(), locked: true, unit: "ml", grams: "1" },
      { id: createId(), locked: true, unit: "fl oz", grams: "29.5735" },
    ];
  }
  return [
    { id: createId(), locked: true, unit: "gm", grams: "1" },
    { id: createId(), locked: true, unit: "oz", grams: "28.3495" },
  ];
};

export const initialUnitConversion: UnitConversionData = {
  rows: buildBaseConversionRows(""),
};

export const validateUnitConversion = (
  data: UnitConversionData,
): UnitConversionErrors => {
  const rowErrors: Record<string, { unit?: string; grams?: string }> = {};
  data.rows.forEach((row) => {
    if (row.locked) return;
    const rErr: { unit?: string; grams?: string } = {};
    if (!isNonEmpty(row.unit)) rErr.unit = "Select a unit";
    if (!isPositiveNumber(row.grams)) rErr.grams = "Enter grams";
    if (Object.keys(rErr).length > 0) rowErrors[row.id] = rErr;
  });
  if (Object.keys(rowErrors).length === 0) return {};
  return { rows: rowErrors };
};

/* ── Combined form data ─────────────────────────────────────── */

export interface AddDishFormData {
  details: DetailsNutritionData;
  conversion: UnitConversionData;
}

/* ── Hydrate the form from a view-mode DishDetail ─────────────── */

// Split "100g" / "100 gm" into a { qty, unit } pair. Falls back to the raw
// value when the input doesn't match the number+unit shape.
const splitBaseQuantity = (raw: string): { qty: string; unit: string } => {
  const trimmed = (raw ?? "").trim();
  if (!trimmed) return { qty: "", unit: "" };
  const match = trimmed.match(/^([\d.]+)\s*([a-zA-Z]+)?$/);
  if (!match) return { qty: trimmed, unit: "" };
  const rawUnit = (match[2] ?? "").toLowerCase();
  const UNIT_ALIASES: Record<string, string> = { g: "gm", grams: "gm", ml: "ml" };
  return { qty: match[1] ?? "", unit: UNIT_ALIASES[rawUnit] ?? rawUnit };
};

const dietTypeToType = (t: DietType): string => {
  if (t === "veg") return "veg";
  if (t === "nonveg") return "nonveg";
  return "";
};

// Detail-endpoint micro values arrive as strings like "150.00"/"0.05" — trim
// the trailing zeros so the input doesn't render padded decimals.
const trimMicroValue = (raw: unknown): string => {
  if (raw === undefined || raw === null || raw === "") return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw);
  return String(parseFloat(n.toFixed(6)));
};

export const buildFormFromDish = (dish: DishDetail): AddDishFormData => {
  const { qty, unit } = splitBaseQuantity(dish.baseQuantity);
  // Hydrate the micro form state from the DishDetail — values are keyed
  // by backend snake_case names, so translate through MICRO_NUTRIENT_BACKEND_KEY.
  const micro: MicroNutrients = { ...initialMicroNutrients };
  if (dish.micronutrients) {
    MICRO_NUTRIENT_FIELDS.forEach((key) => {
      const backendKey = MICRO_NUTRIENT_BACKEND_KEY[key];
      micro[key] = trimMicroValue(dish.micronutrients?.[backendKey]);
    });
  }
  return {
    details: {
      photo: null,
      photoPreview: dish.imageUrl,
      name: dish.name,
      unit,
      baseQty: qty,
      category: dish.category ?? "",
      type: dietTypeToType(dish.dietType),
      calories: String(dish.calories),
      adjProtein: String(dish.adjProtein),
      grossProtein: String(dish.grossProtein ?? ""),
      carbs: String(dish.carbs ?? ""),
      netCarbs: String(dish.netCarbs),
      fat: String(dish.fats),
      gl: String(dish.gl),
      fiber: dish.fiber !== undefined ? String(dish.fiber) : "",
      micro,
      cookingFatDynamic: dish.cookingFatBands.length > 0,
      cookingFatBands:
        dish.cookingFatBands.length > 0
          ? dish.cookingFatBands.map((b) => ({
              id: createId(),
              minQty: b.minQty,
              maxQty: b.maxQty,
              fat: b.fat,
            }))
          : [createEmptyCookingFatBand()],
      recipeIngredients:
        dish.ingredientList.length > 0
          ? dish.ingredientList.map((row) => ({
              id: createId(),
              name: row.name,
              imageUrl: row.imageUrl,
              quantity: row.quantity,
              unit: row.unit,
              cal: row.cal,
              carb: row.carb,
              netCarb: "",
              fats: row.fats,
              aProt: row.aProt,
              gl: row.gl,
              oil: row.oil,
              cpf: row.cpf,
              // Carry the ingredient's food_id through so the Edit page can
              // fetch its measurements + per-100g nutrition and hydrate the
              // unit dropdown / Dish calculator with real scaling data.
              foodId: row.foodId,
            }))
          : [createEmptyIngredientRow()],
      recipeInstructions: dish.recipeInstructions.join("\n"),
      tags: dish.tags ?? [],
    },
    conversion: {
      // Base rows (gm/oz or ml/fl oz) can appear in any position in the API
      // response, so identify them by unit type instead of index. Locked
      // rows come first, then custom rows, so the layout matches the add
      // flow.
      rows: (() => {
        const baseRows = buildBaseConversionRows(unit);
        const baseUnitSet = new Set(
          baseRows.map((r) => r.unit.toLowerCase()),
        );
        const customRows = dish.unitConversions
          .filter((row) => !baseUnitSet.has(row.unit.trim().toLowerCase()))
          .map((row) => {
            // Grams comes formatted as "1 g" / "150 g" — pull just the number.
            const gramsMatch = row.grams.match(/[\d.]+/);
            const grams = gramsMatch ? gramsMatch[0] : row.grams;
            return {
              id: createId(),
              locked: false,
              unit: row.unit,
              grams,
            };
          });
        return [...baseRows, ...customRows];
      })(),
    },
  };
};
